'use client'

import { useCallback, useEffect, useState } from 'react'
import {
  CheckCircle2,
  Clock3,
  FileSearch,
  LayoutDashboard,
  MessageCircle,
  Users,
} from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useTheme } from 'next-themes'

import { Button } from '@/components/ui/button'
import { talentPoolCopy } from '@/app/talent-pool/copy'
import { pushTalentPoolGtmEvent } from './talent-pool-analytics'

type TalentPoolThankYouViewProps = { userName: string | null }

export function TalentPoolThankYouView({
  userName,
}: TalentPoolThankYouViewProps) {
  const { resolvedTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  const copy = talentPoolCopy

  const logoSrc =
    resolvedTheme === 'dark' && mounted
      ? 'https://hyred.sgp1.cdn.digitaloceanspaces.com/assets/dark-logo.svg'
      : 'https://hyred.sgp1.cdn.digitaloceanspaces.com/assets/logo.svg'

  const displayName = userName || 'Kamu'
  const descriptionText = copy.thankYou.description.replace(
    '{name}',
    displayName,
  )

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    pushTalentPoolGtmEvent('talent_pool_registration_complete', {
      page: 'talent-pool',
    })
  }, [])

  const trackThankYouCtaClick = useCallback(
    (target: 'whatsapp' | 'dashboard') => {
      const basePayload = {
        page: 'talent-pool',
        placement: 'thank_you',
        cta_target: target,
      }

      pushTalentPoolGtmEvent('talent_pool_thank_you_cta_click', basePayload)

      if (target === 'whatsapp') {
        pushTalentPoolGtmEvent('talent_pool_thank_you_whatsapp_click', basePayload)
        return
      }

      pushTalentPoolGtmEvent('talent_pool_thank_you_dashboard_click', basePayload)
    },
    [],
  )

  return (
    <div className="relative isolate">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-x-hidden"
      >
        <Image
          src="/assets/ornament/Union-1.svg"
          alt=""
          width={487}
          height={513}
          className="absolute -right-36 -top-24 h-[300px] w-auto opacity-60 sm:-right-32 sm:-top-20 sm:h-[360px] lg:-right-28 lg:-top-14 lg:h-[430px]"
        />
        <Image
          src="/assets/ornament/Union-3.svg"
          alt=""
          width={487}
          height={513}
          className="absolute -left-44 top-[40%] h-[260px] w-auto opacity-40 sm:-left-36 sm:h-[320px] lg:-left-28 lg:top-[45%] lg:h-[350px]"
        />
      </div>

      <nav className="sticky top-0 z-50 border-b border-border-primary bg-white dark:bg-background">
        <div className="mx-auto flex h-20 w-full max-w-[1440px] items-center justify-between px-4 sm:px-6 lg:px-12">
          <Link href="/" aria-label="Hyred home">
            <Image
              src={logoSrc}
              alt="Hyred logo"
              width={136}
              height={42}
              className="h-[42px] w-auto"
              priority
            />
          </Link>
          <div className="flex items-center gap-3">
            <Link
              href="/id/blog"
              className="hidden h-11 items-center rounded-lg px-5 text-base font-semibold text-[#222222] transition-colors hover:bg-[#f4f4f4] md:flex"
            >
              Blog
            </Link>
            <Button
              asChild
              className="h-11 rounded-lg bg-brand-primary px-[18px] text-base font-semibold text-white shadow-button hover:bg-brand-primary-hover"
            >
              <Link href="/">Review CV Gratis</Link>
            </Button>
          </div>
        </div>
      </nav>

      <main className="mx-auto w-full max-w-4xl px-4 pb-16 pt-8 sm:px-6 lg:px-8">
        <section className="relative overflow-hidden rounded-3xl border border-[#dfe7df] bg-[#f4fff7] px-5 py-7 text-center shadow-[0_10px_24px_rgba(0,0,0,0.06)] sm:px-8">
          <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#dff7e8] text-[#1e9e4b]">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h1 className="mt-4 text-[34px] font-semibold tracking-[-0.03em] text-[#1f1f1f] sm:text-[40px]">
            {copy.thankYou.heading}
          </h1>
          <p className="mx-auto mt-2 max-w-2xl text-[15px] leading-[1.7] text-[#4d4d4d]">
            {descriptionText}
          </p>
          <div className="pointer-events-none absolute -right-12 -top-16 h-40 w-40 rounded-full bg-[#1e9e4b]/15 blur-3xl" />
          <div className="pointer-events-none absolute -left-12 -bottom-16 h-40 w-40 rounded-full bg-[#1e9e4b]/10 blur-3xl" />
        </section>

        <section className="mt-8 space-y-4">
          <h2 className="text-[18px] font-semibold tracking-[-0.01em] text-[#1f1f1f]">
            Langkah Selanjutnya
          </h2>

          <div className="grid gap-4 md:grid-cols-2">
            <article className="relative overflow-hidden rounded-2xl border border-[#e3e3e3] bg-white p-5 shadow-[0_8px_20px_rgba(0,0,0,0.06)]">
              <p className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#111111] text-[12px] font-semibold text-white">
                1
              </p>
              <div className="mt-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#f3f3f3] text-[#3b3b3b]">
                <Users className="h-5 w-5" />
              </div>
              <p className="mt-3 text-[21px] font-semibold tracking-[-0.02em] text-[#1f1f1f]">
                {copy.thankYou.whatsapp.title}
              </p>
              <p className="mt-2 text-[14px] leading-[1.6] text-[#666666]">
                {copy.thankYou.whatsapp.description}
              </p>
              <Button
                asChild
                className="mt-5 h-11 w-full rounded-xl bg-[#24bf5a] text-white hover:bg-[#1fa74f]"
              >
                <a
                  id="talent-pool-whatsapp-cta"
                  href={copy.whatsappGroupUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackThankYouCtaClick('whatsapp')
                  }}
                >
                  <MessageCircle className="mr-2 h-4 w-4" />
                  {copy.thankYou.whatsapp.cta}
                </a>
              </Button>
            </article>

            <article className="relative overflow-hidden rounded-2xl border border-[#dfe0ff] bg-[#f7f7ff] p-5 shadow-[0_8px_20px_rgba(0,0,0,0.06)]">
              <p className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#111111] text-[12px] font-semibold text-white">
                2
              </p>
              <div className="mt-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#e5e5ff] text-[#4f46e5]">
                <LayoutDashboard className="h-5 w-5" />
              </div>
              <p className="mt-3 text-[21px] font-semibold tracking-[-0.02em] text-[#1f1f1f]">
                {copy.thankYou.dashboard.title}
              </p>
              <p className="mt-2 text-[14px] leading-[1.6] text-[#666666]">
                {copy.thankYou.dashboard.description}
              </p>
              <div className="pointer-events-none absolute -right-12 -top-16 h-40 w-40 rounded-full bg-[#4f46e5]/15 blur-3xl" />
              <Button
                asChild
                className="mt-5 h-11 w-full rounded-xl bg-[#4f46e5] text-white hover:bg-[#4338ca]"
              >
                <Link
                  id="talent-pool-dashboard-cta"
                  href="/id/dashboard/talent-pool-consent"
                  onClick={() => {
                    trackThankYouCtaClick('dashboard')
                  }}
                >
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  {copy.thankYou.dashboard.cta}
                </Link>
              </Button>
            </article>
          </div>
        </section>

        <section className="mt-8 space-y-4 rounded-2xl border border-[#e4e4e4] bg-white p-5 shadow-[0_8px_20px_rgba(0,0,0,0.06)]">
          <h2 className="text-[18px] font-semibold tracking-[-0.01em] text-[#1f1f1f]">
            {copy.thankYou.nextSteps.title}
          </h2>
          <div className="space-y-4 rounded-xl bg-[#f8f8f8] p-4">
            <div className="flex items-start gap-3">
              <FileSearch className="mt-0.5 h-5 w-5 text-[#1e9e4b]" />
              <div>
                <p className="text-[14px] font-semibold text-[#1f1f1f]">
                  {copy.thankYou.nextSteps.processing.title}
                </p>
                <p className="text-[13px] leading-[1.6] text-[#666666]">
                  {copy.thankYou.nextSteps.processing.description}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock3 className="mt-0.5 h-5 w-5 text-[#1e9e4b]" />
              <div>
                <p className="text-[14px] font-semibold text-[#1f1f1f]">
                  {copy.thankYou.nextSteps.matching.title}
                </p>
                <p className="text-[13px] leading-[1.6] text-[#666666]">
                  {copy.thankYou.nextSteps.matching.description}
                </p>
              </div>
            </div>
          </div>
          <p className="text-center text-[12px] text-[#7b7b7b]">
            {copy.thankYou.support}
          </p>
        </section>
      </main>
    </div>
  )
}
